import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, User, Phone, MapPin, FileText, Truck, Route, Receipt, ShieldCheck, KeyRound,
  Loader2, Lock, Unlock, RotateCw, Mail, Ban, CheckCircle2, ClipboardList,
} from "lucide-react";
import toast from "react-hot-toast";
import { useDriverDetails } from "../hooks/useDriverDetails";
import { useDrivers } from "../hooks/useDrivers";
import { useDriverRequests } from "../hooks/useDriverRequests";

const CATEGORY_LABELS = { toll: "Toll", repair: "Repair", insurance_premium: "Insurance Premium", salary: "Salary", other: "Other" };
const PERMISSION_LABELS = {
  allow_fuel_entry: "Allow Fuel Entry", allow_expense_entry: "Allow Expense Entry",
  allow_maintenance_requests: "Allow Maintenance Requests", allow_leave_requests: "Allow Leave Requests",
  allow_document_download: "Allow Document Download", allow_chat: "Allow Chat",
  allow_trip_acceptance: "Allow Trip Acceptance", allow_notifications: "Allow Notifications",
  allow_pre_trip_inspection: "Allow Pre-Trip Inspection",
};
const REQUEST_TYPE_LABELS = { fuel: "Fuel Log", expense: "Expense", maintenance: "Maintenance", leave: "Leave", inspection: "Inspection" };
const REQUEST_STATUS_STYLE = {
  pending:  { bg: "#FFFBEB", color: "#D97706", label: "Pending" },
  approved: { bg: "#F0FDF4", color: "#16A34A", label: "Approved" },
  rejected: { bg: "#FEF2F2", color: "#DC2626", label: "Rejected" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function Toggle({ checked, onChange }) {
  return (
    <button
      onClick={onChange}
      style={{
        width: 38, height: 22, borderRadius: 99, border: "none", cursor: "pointer",
        background: checked ? "#4F46E5" : "#D1D5DB", position: "relative", transition: "background 0.15s", flexShrink: 0,
      }}
    >
      <span style={{
        position: "absolute", top: 2, left: checked ? 18 : 2, width: 18, height: 18, borderRadius: "50%",
        background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
      }} />
    </button>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <Icon size={15} color="#4F46E5" />
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{title}</div>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0" }}>
      <span style={{ color: "#9CA3AF" }}>{label}</span>
      <span style={{ color: "#111827", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

export default function DriverDetailsPage({ driverId, onClose }) {
  const { details, loading, reload, getDocumentUrl } = useDriverDetails(driverId);
  const { requests: driverRequests, loading: requestsLoading } = useDriverRequests("all", undefined, true, driverId);
  const {
    resetPassword, disableLogin, enableLogin, forcePasswordChange,
    lockAccount, unlockAccount, updatePermissions, setCredentials,
  } = useDrivers();
  const [busy, setBusy] = useState(false);
  const [showCredForm, setShowCredForm] = useState(false);
  const [credEmail, setCredEmail] = useState("");
  const [credPassword, setCredPassword] = useState("");

  async function handleAction(fn, successMsg) {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.error || "Action failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleResetPassword() {
    setBusy(true);
    try {
      const result = await resetPassword(driverId, true);
      toast.success(`New temporary password: ${result.tempPassword}`, { duration: 10000 });
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  }

  async function handleSetCredentials(e) {
    e.preventDefault();
    if (!credEmail.trim() || !credPassword.trim()) return toast.error("Email and password are required");
    setBusy(true);
    try {
      await setCredentials(driverId, { email: credEmail.trim(), tempPassword: credPassword.trim(), sendCredentialsEmail: true });
      toast.success("Login credentials created");
      setShowCredForm(false);
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to set credentials");
    } finally {
      setBusy(false);
    }
  }

  async function handleTogglePermission(key) {
    setBusy(true);
    try {
      await updatePermissions(driverId, { [key]: !details.permissions?.[key] });
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update permission");
    } finally {
      setBusy(false);
    }
  }

  async function handleViewDocument(which) {
    try {
      const url = await getDocumentUrl(which);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err.response?.data?.error || "No document on file");
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{ position: "fixed", inset: 0, zIndex: 850, background: "#F9FAFB", display: "flex", flexDirection: "column" }}
      >
        <div style={{
          flexShrink: 0, background: "#fff", borderBottom: "1px solid #E5E7EB",
          padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12, background: "#EEF2FF", color: "#4F46E5",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}><User size={20} /></div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#111827" }}>{details?.name || "Driver"}</div>
              <div style={{ display: "flex", gap: 12, fontSize: 12.5, color: "#6B7280", marginTop: 2 }}>
                {details?.employeeId && <span>{details.employeeId}</span>}
                {details?.status && (
                  <span style={{ color: details.status === "active" ? "#16A34A" : "#DC2626", fontWeight: 600, textTransform: "capitalize" }}>
                    {details.status}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, width: 36, height: 36, cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}
          ><X size={16} /></button>
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px", display: "flex", flexDirection: "column", gap: 14 }}>
            {loading || !details ? (
              <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <Loader2 size={18} style={{ animation: "spin 1s linear infinite" }} /> Loading driver profile…
              </div>
            ) : (
              <>
                <Section icon={User} title="Personal Information">
                  <InfoRow label="Phone" value={details.phone} />
                  <InfoRow label="Alternate Contact" value={details.alternateContact} />
                  <InfoRow label="Email" value={details.email} />
                  <InfoRow label="Date of Birth" value={fmtDate(details.dateOfBirth)} />
                  <InfoRow label="Blood Group" value={details.bloodGroup} />
                  <InfoRow label="Experience" value={details.experienceYears != null ? `${details.experienceYears} years` : null} />
                  <InfoRow label="Joining Date" value={fmtDate(details.joiningDate)} />
                  <InfoRow label="Address" value={[details.address, details.city, details.state, details.pinCode].filter(Boolean).join(", ")} />
                  <InfoRow label="Emergency Contact" value={details.emergencyContactName ? `${details.emergencyContactName} (${details.emergencyRelation || "—"}) — ${details.emergencyContactPhone || "—"}` : null} />
                  <InfoRow label="Aadhaar" value={details.aadhaarNumber} />
                  <InfoRow label="PAN" value={details.panNumber} />
                  <InfoRow label="License Number" value={details.licenseNumber} />
                  <InfoRow label="License Expiry" value={fmtDate(details.licenseExpiry)} />
                  <InfoRow label="Medical Cert. Expiry" value={fmtDate(details.medicalCertExpiry)} />
                  {details.notes && <div style={{ marginTop: 8, fontSize: 12.5, color: "#6B7280", fontStyle: "italic" }}>{details.notes}</div>}
                </Section>

                <Section icon={FileText} title="Documents">
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {[["id-proof", "ID Proof", details.hasIdProof], ["license", "Driving License", details.hasLicenseDoc], ["medical-cert", "Medical Certificate", details.hasMedicalCert]].map(([key, label, has]) => (
                      <button
                        key={key}
                        disabled={!has}
                        onClick={() => handleViewDocument(key)}
                        style={{
                          display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600,
                          padding: "7px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
                          background: has ? "#fff" : "#F9FAFB", color: has ? "#4F46E5" : "#D1D5DB",
                          cursor: has ? "pointer" : "default",
                        }}
                      ><FileText size={13} /> {label}{!has && " (none)"}</button>
                    ))}
                  </div>
                </Section>

                <Section icon={Truck} title="Assigned Vehicle">
                  {details.assignedVehicleId
                    ? <InfoRow label="Vehicle" value={details.assignedVehiclePlate} />
                    : <div style={{ fontSize: 13, color: "#9CA3AF" }}>No vehicle assigned.</div>}
                </Section>

                <Section icon={ClipboardList} title="Recent Requests">
                  {requestsLoading ? (
                    <div style={{ fontSize: 13, color: "#9CA3AF" }}>Loading…</div>
                  ) : !driverRequests.length ? (
                    <div style={{ fontSize: 13, color: "#9CA3AF" }}>No requests submitted yet.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {driverRequests.slice(0, 5).map(r => {
                        const st = REQUEST_STATUS_STYLE[r.status];
                        return (
                          <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid #F3F4F6" }}>
                            <span style={{ fontSize: 13, color: "#374151" }}>{REQUEST_TYPE_LABELS[r.type]}</span>
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: st.color, background: st.bg, borderRadius: 5, padding: "2px 8px" }}>{st.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Section>

                <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                  <div style={{ flex: "1 1 260px" }}>
                    <Section icon={Route} title="Trip Summary">
                      <InfoRow label="Total Trips" value={details.tripSummary?.total} />
                      <InfoRow label="Completed" value={details.tripSummary?.completed} />
                      <InfoRow label="Cancelled" value={details.tripSummary?.cancelled} />
                    </Section>
                  </div>
                  <div style={{ flex: "1 1 260px" }}>
                    <Section icon={Receipt} title="Expenses">
                      {details.expensesByCategory?.length ? details.expensesByCategory.map(e => (
                        <InfoRow key={e.category} label={CATEGORY_LABELS[e.category] || e.category} value={`₹${e.total.toLocaleString("en-IN")}`} />
                      )) : <div style={{ fontSize: 13, color: "#9CA3AF" }}>No expenses logged.</div>}
                    </Section>
                  </div>
                </div>

                <Section icon={KeyRound} title="Login & Credentials">
                  {!details.loginEmail ? (
                    showCredForm ? (
                      <form onSubmit={handleSetCredentials} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <input placeholder="Driver login email" value={credEmail} onChange={e => setCredEmail(e.target.value)}
                          style={{ height: 36, borderRadius: 8, border: "1px solid #E5E7EB", padding: "0 10px", fontSize: 13 }} />
                        <input placeholder="Temporary password" value={credPassword} onChange={e => setCredPassword(e.target.value)}
                          style={{ height: 36, borderRadius: 8, border: "1px solid #E5E7EB", padding: "0 10px", fontSize: 13 }} />
                        <button type="submit" disabled={busy} style={{ height: 36, borderRadius: 8, border: "none", background: "#4F46E5", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                          Create Login
                        </button>
                      </form>
                    ) : (
                      <button onClick={() => setShowCredForm(true)} style={{ fontSize: 13, fontWeight: 600, color: "#4F46E5", background: "none", border: "none", cursor: "pointer" }}>
                        + Set up Driver Portal login
                      </button>
                    )
                  ) : (
                    <>
                      <InfoRow label="Login Email" value={details.loginEmail} />
                      <InfoRow label="Last Login" value={details.lastLoginAt ? fmtDate(details.lastLoginAt) : "Never"} />
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                        <ActionButton icon={RotateCw} label="Reset Password" onClick={handleResetPassword} disabled={busy} />
                        {details.isLoginEnabled
                          ? <ActionButton icon={Ban} label="Disable Login" danger onClick={() => handleAction(() => disableLogin(driverId), "Login disabled")} disabled={busy} />
                          : <ActionButton icon={CheckCircle2} label="Enable Login" onClick={() => handleAction(() => enableLogin(driverId), "Login enabled")} disabled={busy} />}
                        {details.isLocked
                          ? <ActionButton icon={Unlock} label="Unlock Account" onClick={() => handleAction(() => unlockAccount(driverId), "Account unlocked")} disabled={busy} />
                          : <ActionButton icon={Lock} label="Lock Account" danger onClick={() => handleAction(() => lockAccount(driverId), "Account locked")} disabled={busy} />}
                        <ActionButton icon={Mail} label="Force Password Change" onClick={() => handleAction(() => forcePasswordChange(driverId), "Driver must change password on next login")} disabled={busy} />
                      </div>
                    </>
                  )}
                </Section>

                <Section icon={ShieldCheck} title="Permissions">
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid #F3F4F6" }}>
                        <span style={{ fontSize: 13, color: "#374151" }}>{label}</span>
                        <Toggle checked={details.permissions?.[key] ?? true} onChange={() => handleTogglePermission(key)} />
                      </div>
                    ))}
                  </div>
                </Section>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function ActionButton({ icon: Icon, label, onClick, disabled, danger }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600,
        padding: "7px 12px", borderRadius: 8, border: `1px solid ${danger ? "#FECACA" : "#E5E7EB"}`,
        background: "#fff", color: danger ? "#DC2626" : "#4F46E5", cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    ><Icon size={13} /> {label}</button>
  );
}
